/**
 * LedgerSearchAutocomplete Component
 * Autocomplete for selecting jobs (orders) or invoices to open their ledgers
 */

import React, { useState, useEffect, useCallback } from "react";
import {
  Autocomplete,
  TextField,
  Box,
  Typography,
  CircularProgress,
  Chip,
} from "@mui/material";
import { LocalShipping, Receipt } from "@mui/icons-material";
import { searchEntities } from "../../followups/services/entitySearchService.js";
import { logError } from "@/shared/utils/logger.js";

/**
 * LedgerSearchAutocomplete Component
 * @param {Object} props
 * @param {string|null} props.value - Selected ledger ID (job ID or invoice ID)
 * @param {Function} props.onChange - Callback when ledger is selected (receives ID string)
 * @param {string} [props.ledgerType] - Filter by type: "auto" | "job" | "invoice"
 * @param {string} [props.label] - Label for the field
 * @param {boolean} [props.disabled] - Whether field is disabled
 * @param {string} [props.placeholder] - Placeholder text
 * @param {Object} [props.sx] - Additional sx styles
 * @param {Function} [props.onKeyDown] - Key down handler
 */
const LedgerSearchAutocomplete = ({
  value,
  onChange,
  ledgerType = "auto",
  label = "Job ID or Invoice ID",
  disabled = false,
  placeholder = "e.g. BUB-20260128-001 or INV-20260128-001",
  sx,
  onKeyDown,
  ...otherProps
}) => {
  const [inputValue, setInputValue] = useState("");
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);

  // Update input value when value prop changes (when ID is set externally)
  useEffect(() => {
    if (value) {
      setInputValue(value);
    } else {
      setInputValue("");
    }
  }, [value]);

  // Debounced search function
  const search = useCallback(
    async (searchTerm) => {
      if (!searchTerm || searchTerm.trim().length < 2) {
        setOptions([]);
        return;
      }

      setLoading(true);
      try {
        // Search for entities (orders and invoices)
        const results = await searchEntities(searchTerm, {
          limitPerType: 10,
          maxTotal: 20,
        });

        // Filter to only orders (jobs) and invoices
        const filteredResults = results.filter(
          (entity) =>
            entity.entityType === "order" || entity.entityType === "invoice"
        );

        // Further filter by ledgerType if not "auto"
        let finalResults = filteredResults;
        if (ledgerType === "job") {
          finalResults = filteredResults.filter(
            (entity) => entity.entityType === "order"
          );
        } else if (ledgerType === "invoice") {
          finalResults = filteredResults.filter(
            (entity) => entity.entityType === "invoice"
          );
        }

        // Transform results to include entityId as the main identifier
        const transformedResults = finalResults.map((entity) => ({
          ...entity,
          id: entity.entityId, // Ensure id is available
          displayName: entity.displayName || entity.entityId,
        }));

        setOptions(transformedResults);
      } catch (err) {
        logError("Error searching ledgers", {
          searchTerm,
          ledgerType,
          error: err.message,
        });
        setOptions([]);
      } finally {
        setLoading(false);
      }
    },
    [ledgerType]
  );

  // Handle input change with debounce
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (inputValue) {
        search(inputValue);
      } else {
        setOptions([]);
      }
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [inputValue, search]);

  // Handle selection change - extract ID and call onChange
  const handleChange = (event, newValue) => {
    if (newValue) {
      // Extract the ID from the selected entity
      const selectedId = newValue.entityId || newValue.id || newValue.displayName;
      onChange(selectedId);
      setInputValue(selectedId);
    } else {
      // Cleared
      onChange(null);
      setInputValue("");
    }
  };

  // Get display value for the autocomplete
  const displayValue = value
    ? options.find((opt) => opt.entityId === value || opt.id === value) || null
    : null;

  // Get icon for entity type
  const getEntityIcon = (entityType) => {
    switch (entityType) {
      case "order":
        return <LocalShipping fontSize="small" />;
      case "invoice":
        return <Receipt fontSize="small" />;
      default:
        return <LocalShipping fontSize="small" />;
    }
  };

  // Get color for entity type
  const getEntityColor = (entityType) => {
    switch (entityType) {
      case "order":
        return "info";
      case "invoice":
        return "warning";
      default:
        return "default";
    }
  };

  return (
    <Box sx={{ width: "100%", ...sx }}>
      <Autocomplete
        disabled={disabled}
        value={displayValue}
        onChange={handleChange}
        inputValue={inputValue}
        onInputChange={(event, newInputValue, reason) => {
          // Allow manual typing
          if (reason === "input" || reason === "clear") {
            setInputValue(newInputValue);
            // If user clears or types, update the parent value
            if (reason === "clear") {
              onChange(null);
            } else if (newInputValue && newInputValue.trim().length > 0) {
              // Allow manual entry - pass the typed value directly
              onChange(newInputValue.trim());
            }
          }
        }}
        options={options}
        getOptionLabel={(option) => {
          if (typeof option === "string") return option;
          return option?.displayName || option?.entityId || option?.id || "";
        }}
        isOptionEqualToValue={(option, value) => {
          if (!option || !value) return false;
          if (typeof value === "string") {
            return option.entityId === value || option.id === value;
          }
          return (
            option.entityId === value.entityId &&
            option.entityType === value.entityType
          );
        }}
        loading={loading}
        filterOptions={(x) => x} // Disable client-side filtering, we do server-side
        freeSolo // Allow manual entry of IDs
        size="small"
        fullWidth
        renderInput={(params) => (
          <TextField
            {...params}
            label={label}
            placeholder={placeholder}
            onKeyDown={onKeyDown}
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {loading ? (
                    <CircularProgress color="inherit" size={20} />
                  ) : null}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
          />
        )}
      renderOption={(props, option) => {
        // Handle string options (manual entry)
        if (typeof option === "string") {
          return (
            <Box component="li" {...props} key={option}>
              <Typography variant="body2">{option}</Typography>
            </Box>
          );
        }

        // Handle entity options
        return (
          <Box component="li" {...props} key={option.entityId || option.id}>
            <Box
              sx={{ display: "flex", alignItems: "center", gap: 1, width: "100%" }}
            >
              <Box sx={{ color: `${getEntityColor(option.entityType)}.main` }}>
                {getEntityIcon(option.entityType)}
              </Box>
              <Box sx={{ flex: 1 }}>
                <Box
                  sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}
                >
                  <Typography variant="body2" fontWeight="medium">
                    {option.displayName || option.entityId}
                  </Typography>
                  <Chip
                    label={option.entityType === "order" ? "Job" : "Invoice"}
                    size="small"
                    color={getEntityColor(option.entityType)}
                    sx={{ height: 20, fontSize: "0.7rem", textTransform: "capitalize" }}
                  />
                </Box>
                {option.subtitle && (
                  <Typography variant="caption" color="text.secondary">
                    {option.subtitle}
                  </Typography>
                )}
              </Box>
            </Box>
          </Box>
        );
      }}
        noOptionsText={
          inputValue.trim().length < 2
            ? "Type at least 2 characters to search"
            : "No jobs or invoices found"
        }
        {...otherProps}
      />
    </Box>
  );
};

export default LedgerSearchAutocomplete;
